import {
  CompanyDomain,
  DateFormatType,
  PUBLIC_URL,
  UNKNOWN_COMPANY_LOGO,
} from '@converge/data';
import moment, {
  Duration,
  Moment,
  MomentInput,
  duration,
  unitOfTime,
} from 'moment-timezone';

import { NumberFormatValues } from 'react-number-format/types/types';
import { handleError } from './errors';

export enum TaskLabel {
  RenewalIssuance = 'RENEWAL_ISSUANCE',
  RenewalNotice = 'RENEWAL_NOTICE',
  RenewalReview = 'RENEWAL_REVIEW',
  RenewalSolicitation = 'RENEWAL_SOLICITATION',
}

export const formatTransactionType = (type: string): string =>
  type.replace(/_/g, ' ').toUpperCase();

export function displayFormatDate(
  date?: string | Date | Moment,
  format = DateFormatType.USADateFormat
) {
  if (!date) {
    return '';
  }
  return moment(date).format(format);
}

export function displayFormatDateUTC(
  date?: string | Date | Moment,
  format = DateFormatType.USADateFormat
) {
  if (!date) {
    return '';
  }
  return moment(date).utc().format(format);
}

export function displayDateTime(date?: string | Date | Moment) {
  if (!date) {
    return '';
  }
  return moment(date).utc().format('h:mm a');
}
export function NewDisplayFormatDate(
  date?: string | Date | Moment,
  format = DateFormatType.USANewFormat
) {
  if (!date) {
    return '';
  }
  return moment(date).utc().format(format);
}

displayFormatDate.DateFormatType = DateFormatType;

export function displayNumber(value?: number) {
  if (value === undefined || value === null) {
    return 'NaN';
  }

  // Convert numeric strings to numbers
  if (typeof value === 'string' && !isNaN(Number(value))) {
    value = Number(value);
  }

  if (typeof value !== 'number' || isNaN(value)) {
    return 'NaN';
  }
  if (value % 1 !== 0) {
    return value.toFixed(2); // Display with one decimal place if it has a decimal part
  } else {
    return value.toFixed(0); // Display without decimal places if it's a whole number
  }
}

export function checkPositiveNumber(values: NumberFormatValues) {
  const { value, floatValue } = values;
  return !value.includes('-') && (floatValue === undefined || floatValue >= 0);
}

export function capitalizeFirstLetterRemoveUnderLine(str: string) {
  const result = str.replace(/_/g, ' ');
  return result.charAt(0).toUpperCase() + result.slice(1).toLowerCase();
}

export function capitalizeLetterRemoveUnderLine(str: string) {
  const minorWords = ['of', 'and', 'in', 'on', 'for', 'the', 'a', 'an'];

  const result = str.replace(/_/g, ' ');

  return result
    .split(' ')
    .map((word, index) => {
      const lowerWord = word.toLowerCase();
      if (index !== 0 && minorWords.includes(lowerWord)) {
        return lowerWord;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
const taskLabelMap = {
  [TaskLabel.RenewalIssuance]: 'Renewal Issuance',
  [TaskLabel.RenewalNotice]: 'Renewal Notice',
  [TaskLabel.RenewalReview]: 'Renewal Review',
  [TaskLabel.RenewalSolicitation]: 'Renewal Solicitation',
};
export const getTaskLabel = (taskLabel?: TaskLabel | null) => {
  return (taskLabel && taskLabelMap[taskLabel]) || '--';
};

export function capitalizeAndSplitWords(str: string) {
  if (!str) return '';

  // Add space before capital letters and handle the first character
  const withSpaces = str.replace(/([A-Z])/g, ' $1').trim();

  // Split into words, capitalize first letter of each word, and join
  return withSpaces
    .split(' ')
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function capitalizeWordsExceptPreposition(sentence: string) {
  const exceptions = ['and', 'of', 'the', 'in', 'on', 'at']; // Add more exceptions as needed

  return sentence
    .toLowerCase()
    .split(' ')
    .map((word) => {
      if (exceptions.includes(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}
export function displayDateFromNow(
  date: string,
  {
    showTime = false,
    ifOlderThanShowDate = true,
    ifOlderThanUnitShowDate = 'hours',
    format = DateFormatType.USADateFormatWithTimeAndSecondsAt,
    datePrefix,
  }: {
    ifOlderThanShowDate?: number | boolean;
    ifOlderThanUnitShowDate?: unitOfTime.Diff;
    format?: DateFormatType;
    showTime?: boolean;
    datePrefix?: string;
  } = {}
) {
  if (!date) {
    return '';
  }

  const dateMoment = moment(date).utc();

  if (
    (ifOlderThanShowDate === true ?? typeof ifOlderThanShowDate === 'number') &&
    moment().diff(dateMoment, ifOlderThanUnitShowDate) >
      (typeof ifOlderThanShowDate === 'boolean' ? 24 : ifOlderThanShowDate)
  ) {
    const formattedDate = displayFormatDate(dateMoment, format);

    if (datePrefix) {
      return `${datePrefix} ${formattedDate}`;
    }

    return formattedDate;
  }

  if (showTime) {
    return [dateMoment.fromNow(), 'at', dateMoment.format('LTS')].join(' ');
  }

  return dateMoment.fromNow();
}

export function addYear(date: string | Moment | undefined | null) {
  if (!date) {
    return '';
  }

  const originalDate = moment(date);

  const nextYearDate = originalDate
    .clone()
    .startOf('day')
    .add(1, 'year')
    .set({ hour: 0, minute: 1, second: 0, millisecond: 0 })
    .format('YYYY-MM-DDTHH:mm:ss.SSS');

  return nextYearDate;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function addDays(date: Date | string, days: number): any {
  if (!date) {
    return undefined;
  }
  return moment(date)
    .add(days, 'days')
    .set({ hour: 0, minute: 1, second: 0, millisecond: 0 });
}

export function getLogoForDomain(domain?: string): string {
  if (!domain) {
    return UNKNOWN_COMPANY_LOGO;
  }

  switch (domain) {
    case CompanyDomain.Converge:
      return `${PUBLIC_URL}/logo.svg`;
    default:
      return `https://logo.clearbit.com/${domain}`;
  }
}

export function convertInputToFormattedAmountUSD(value: unknown) {
  let parsedValue: number | null = null;
  if (typeof value === 'number') {
    parsedValue = value;
  } else if (typeof value === 'string') {
    parsedValue = parseFloat(value);
  }

  if (!parsedValue || isNaN(parsedValue)) {
    return '';
  }

  return formatDollarAmount(parsedValue);
}

export function formatDollarAmount(
  input: number | null | undefined,
  showCents = false,
  addParenthesis = false
) {
  if (input === undefined || input === null) {
    return '';
  }
  const formatted = input.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  });
  return addParenthesis ? `(${formatted})` : formatted;
}

export const formatPercentageAmount = (providerPercentage: number) => {
  return `${(+providerPercentage * 100).toFixed(2).replace(/\.?0+$/, '')} %`;
};

export function formatUsdWithCents(input: number | null | undefined) {
  return formatDollarAmount(input, true);
}

export const formatPercentWithoutTrailingZeros = (
  value: string | number,
  fractionDigits = 2
) =>
  `${(Number(value) * 100)
    .toFixed(fractionDigits)
    .replace(/0+$/, '')
    .replace(/\.$/, '')}%`;

export const roundNumber = (input: number | null | undefined) => {
  if (input === undefined || input === null) {
    return input;
  }
  return Math.round(input);
};

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const daysAgo = (
  input: string,
  beforeToday = 'overdue',
  afterToday = ''
) => {
  const dueDate = moment(input);
  const today = moment(new Date());
  if (dueDate.isBefore()) {
    return formatDaysAgo(
      Math.floor(moment.duration(today.diff(dueDate)).asDays()),
      beforeToday
    );
  } else {
    return formatDaysAgo(
      Math.ceil(moment.duration(dueDate.diff(today)).asDays()),
      afterToday
    );
  }
};

export const formatDaysAgo = (input: number, extraString = '') => {
  const days = Math.floor(input);
  if (input === 1) {
    return `${days} day ${extraString}`.trim();
  } else {
    return `${days} days ${extraString}`.trim();
  }
};

export const formatPercent = (input: number) => {
  // Convert the input to a string with 2 significant digits
  // Remove trailing zeroes
  if (typeof input !== 'number') {
    handleError({
      source: 'formatPercent',
      message: `formatPercent called with non-number input: ${input}`,
      context: {
        data: {
          input,
        },
      },
    });
  }

  if (isNaN(input)) {
    return '0%';
  }

  return `${(typeof input === 'number' ? input : 0)
    .toFixed(2)
    .replace(/0+$/, '')
    .replace(/\.$/, '')}%`;
};

export const convertStringToTriStateBoolean = (
  input: string | undefined | null
) => {
  if (input === 'true') {
    return true;
  } else if (input === 'false') {
    return false;
  } else {
    return null;
  }
};

export const bytesToSize = (bytes: number): string => {
  const sizes = ['Bytes', 'Kb', 'Mb', 'Gb', 'Tb'];
  if (bytes === 0) return 'n/a';
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    sizes.length - 1
  );
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
};

export const bitToYesNo = (input: string | number | undefined | null) => {
  return input?.toString() === '1' ? 'Yes' : 'No';
};

export const sortByOrder = (
  a: { order?: number | null },
  b: { order?: number | null }
) => {
  return (a.order ?? Infinity) - (b.order ?? Infinity);
};

export const sortByName = (
  a: { name?: string | null },
  b: { name?: string | null }
) => {
  return (a.name ?? 'zzzz').localeCompare(b.name ?? 'zzzz');
};

export interface IOnloadOptions
  extends Record<string, number | string | Headers | null> {
  status: number;
  statusText: string;
  headers: Headers;
}
export interface ICustomFetchOptions extends RequestInit {
  useUpload?: boolean;
  onProgress?: (ev: ProgressEvent) => void;
  onAbortPossible?: (abortHandler: unknown) => void;
}

export const parseHeaders = (rawHeaders: string): Headers => {
  const headers = new Headers();
  const preProcessedHeaders = rawHeaders.replace(/\r?\n[\t ]+/g, ' ');
  preProcessedHeaders.split(/\r?\n/).forEach((line: string) => {
    const parts = line.split(':');
    const key = parts.shift()?.trim();
    if (key) {
      const value = parts.join(':').trim();
      headers.append(key, value);
    }
  });
  return headers as Headers;
};

export const customFetch = (
  url: URL | RequestInfo,
  options: ICustomFetchOptions
): Promise<Response> => {
  if (!options.useUpload) {
    return fetch(url, options);
  }
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const opts: IOnloadOptions = {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: parseHeaders(xhr.getAllResponseHeaders() || ''),
      };
      opts['url'] =
        'responseURL' in xhr
          ? xhr.responseURL
          : opts.headers.get('X-Request-URL');
      const body =
        'response' in xhr ? xhr.response : (xhr as XMLHttpRequest).responseText;
      resolve(new Response(body, opts));
    };
    xhr.onerror = () => {
      reject(new TypeError('Network request failed'));
    };
    xhr.ontimeout = () => {
      reject(new TypeError('Network request failed'));
    };
    xhr.open(options.method || '', url as string, true);

    Object.keys(options.headers as Headers).forEach((key) => {
      const headerValue = options.headers
        ? (options.headers[key as keyof HeadersInit] as string)
        : '';
      xhr.setRequestHeader(key, headerValue);
    });

    if (options.onProgress) {
      xhr.upload.onprogress = options.onProgress;
    }
    if (options.onAbortPossible) {
      options.onAbortPossible(() => xhr.abort());
    }

    xhr.send(
      options.body as XMLHttpRequestBodyInit | Document | null | undefined
    );
  });
};

export const openInNewTab = (url: string): void => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};

export const isNotNullOrUndefined = <T>(
  value: T | null | undefined
): value is T => {
  return value !== null && value !== undefined;
};

export const isNullOrUndefined = <T>(
  value: T | null | undefined
): value is null | undefined => {
  return value === null || value === undefined;
};

export const isString = (value: unknown): value is string => {
  return typeof value === 'string';
};

export const isArray = <T>(value: unknown): value is T[] => {
  return Array.isArray(value);
};

export const isObject = (value: unknown): value is object => {
  return typeof value === 'object';
};

export const compareBySeverity = <T extends { value: string }>(
  a: T,
  b: T
): number => {
  const severityOrder: Record<string, number> = {
    VERY_LOW: 1,
    LOW: 2,
    MODERATE: 3,
    HIGH: 4,
    VERY_HIGH: 5,
  };

  return severityOrder[a.value] - severityOrder[b.value];
};

export function formatDuration(duration: Duration) {
  const parts: string[] = [];

  // return nothing when the duration is falsy or not correctly parsed (P0D)
  if (!duration || duration.toISOString() === 'P0D') return 'N/A';

  if (duration.years() >= 1) {
    const years = Math.floor(duration.years());
    parts.push(years + ' ' + (years > 1 ? 'years' : 'year'));
  }

  if (duration.months() >= 1) {
    const months = Math.floor(duration.months());
    parts.push(months + ' ' + (months > 1 ? 'months' : 'month'));
  }

  if (duration.days() >= 1) {
    const days = Math.floor(duration.days());
    parts.push(days + ' ' + (days > 1 ? 'days' : 'day'));
  }

  if (duration.hours() >= 1) {
    const hours = Math.floor(duration.hours());
    parts.push(hours + ' ' + (hours > 1 ? 'hours' : 'hour'));
  }

  if (duration.minutes() >= 1) {
    const minutes = Math.floor(duration.minutes());
    parts.push(minutes + ' ' + (minutes > 1 ? 'minutes' : 'minute'));
  }

  if (duration.seconds() >= 1 || parts.length === 0) {
    const seconds = Math.floor(duration.seconds());
    parts.push(
      seconds + ' ' + (seconds > 1 || seconds === 0 ? 'seconds' : 'second')
    );
  }

  if (duration.milliseconds() >= 1 || parts.length === 0) {
    const milliseconds = Math.floor(duration.milliseconds());
    parts.push(milliseconds + ' ms');
  }

  return parts.join(', ');
}

export const createDurationBetween = (
  from: MomentInput,
  to: MomentInput = new Date()
) => duration(moment(from).diff(to));

export const formatDurationBetween = (
  from: MomentInput,
  to: MomentInput = new Date()
) => formatDuration(createDurationBetween(from, to));

export const formatList = (
  list: (string | number)[],
  lastSeparator = 'and'
) => {
  return list.reduce((acc, item, index) => {
    if (index === 0) {
      return item;
    }
    if (index === list.length - 1) {
      return `${acc}, ${lastSeparator} ${item}`;
    }
    return `${acc}, ${item}`;
  }, '');
};

export const formatListOfObjectValues = (
  obj: Record<string, string | number>,
  lastSeparator = 'and'
) => formatList(Object.values(obj), lastSeparator);

export const formatListOfObjectKeys = (
  obj: Record<string, unknown>,
  lastSeparator = 'and'
) => formatList(Object.keys(obj), lastSeparator);

export const scrollToFirstError = (
  formMethods: { formState: { errors: Record<string, unknown> } },
  timeout = 100
): void => {
  setTimeout(() => {
    const errors = formMethods.formState.errors;
    if (Object.keys(errors).length > 0) {
      // Get the first error field name
      const firstErrorField = Object.keys(errors)[0];

      // Find the element with the error
      const errorElement = document.querySelector(
        `[name="${firstErrorField}"]`
      );
      if (errorElement) {
        // Scroll to the error element with some offset for better visibility
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, timeout);
};

export const normalizeNotes = (text: string): string => {
  return text.replace(/(?<!\n)\n(?!\n)/g, ' ').replace(/\\'/g, "'");
};
